/**
 * When the format-ceiling step-down must NOT replay (Paul, HEOS zone-hop Oct 21 2026).
 * A queued play flushed by a zone switch was read as a hi-res rejection and replayed on the new
 * zone. The one lower-tier replay now needs: the play really reached the device, a real
 * rejection (not a queue flush / newer user play), the same zone + track still current and not
 * already PLAYING, and no earlier step-down for this (player, track) this session.
 */
import { useMaestroStore } from '../store/maestroStore';
import { isPlayTrackCurrent } from './queuePlayTrackGuards';
import { isPlayUriNotSentError } from './playUriSupersede';

type GuardedAttempt = {
  renderer: { id: string };
  trackId: string;
  dispatched?: boolean;
  staleReason?: () => string | null;
};

const replayedKeys = new Set<string>();
const key = (rendererId: string, trackId: string) => `${rendererId}|${trackId}`;

export function resetReplayGuardForTests(): void {
  replayedKeys.clear();
}

export function noteReplayClaimed(rendererId: string, trackId: string): void {
  replayedKeys.add(key(rendererId, trackId));
}

/** Reason the step-down may not replay this attempt, or null. */
export function replayBlockedReason(attempt: GuardedAttempt, errMsg: string | null): string | null {
  if (replayedKeys.has(key(attempt.renderer.id, attempt.trackId))) return 'replay_already_used';
  if (isPlayUriNotSentError(errMsg)) return 'not_sent';
  if (!attempt.dispatched) return 'never_dispatched';
  return attempt.staleReason?.() ?? null;
}

/** QueueController: why a replay of this play is stale (newer play, zone/track changed, already PLAYING). */
export function formatReplayStaleReason(token: number, rendererId: string, trackId: unknown): string | null {
  if (!isPlayTrackCurrent(token)) return 'superseded_by_newer_play';
  const s = useMaestroStore.getState() as any;
  const activeId = s.activeConnectorRenderer?.id;
  if (activeId && activeId !== rendererId) return 'zone_changed';
  const cur = s.currentTrack?.id;
  if (cur != null && trackId != null && String(cur) !== String(trackId)) return 'track_changed';
  if (s.playbackState === 'playing' && cur != null && String(cur) === String(trackId)) return 'already_playing';
  return null;
}
